<script setup>
/**
 * BannerAd - 広告バナーコンポーネント
 * 
 * 使用例:
 * <BannerAd 
 *   productName="商品名"
 *   discount="20%OFF"
 *   backgroundImage="/path/to/image.jpg"
 *   backgroundColor="#f5f5f5"
 *   size="80"
 *   borderRadius="20"
 *   url="https://example.com"
 *   :openInNewTab="true"
 *   @click="handleClick"
 * />
 */
import { computed } from 'vue';

// eslint-disable-next-line no-undef
const props = defineProps({
  /**
   * 商品名
   */
  productName: {
    type: String,
    default: ''
  },
  
  /**
   * 割引情報（例: "20%OFF"）
   */
  discount: {
    type: String,
    default: ''
  },
  
  /**
   * 背景画像URL
   */
  backgroundImage: {
    type: String,
    default: ''
  },
  
  /**
   * 背景色またはグラデーション（画像がない場合に使用）
   */
  backgroundColor: {
    type: String,
    default: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
  },
  
  /**
   * バナーのサイズ（幅と高さ）
   */
  size: {
    type: [Number, String],
    default: 80
  },
  
  /**
   * 角丸の大きさ
   */
  borderRadius: {
    type: [Number, String],
    default: 20
  },
  
  /**
   * クリック時の遷移先URL
   */
  url: {
    type: String,
    default: ''
  },
  
  /**
   * 新規タブで開くかどうか
   */
  openInNewTab: {
    type: Boolean,
    default: false
  }
});

// eslint-disable-next-line no-undef
const emit = defineEmits(['click']);

// アイコンのスタイル計算
const iconStyles = computed(() => {
  const styles = {
    width: `${props.size}px`,
    height: `${props.size}px`,
    borderRadius: `${props.borderRadius}px`,
  };

  if (props.backgroundImage) {
    styles.backgroundImage = `url(${props.backgroundImage})`;
    styles.backgroundSize = 'cover';
    styles.backgroundPosition = 'center';
  } else if (props.backgroundColor) {
    styles.background = props.backgroundColor;
  }

  return styles;
});

// デバッグ用：クリックカウンター
let adClickCounter = 0;

// 商品ページに遷移する関数
const handleClick = (event) => {
  // カウンターを増やす（デバッグ用）
  adClickCounter++;
  const currentClick = adClickCounter;
  
  // ログ出力
  console.log(`[BannerAd] クリック検出 #${currentClick} - URL: ${props.url}`);
  
  // イベントのデフォルト動作と伝播を停止
  if (event) {
    event.preventDefault();
    
    // イベントの詳細情報を出力
    console.log(`[BannerAd] #${currentClick} - イベントタイプ: ${event.type}`);
  }
  
  // バナークリックデータを作成
  const bannerData = {
    url: props.url,
    productName: props.productName,
    openInNewTab: true, // 常に新規タブで開くように
    timestamp: new Date().toISOString()
  };
  
  // 親コンポーネントにイベントを発火
  console.log(`[BannerAd] #${currentClick} - clickイベント発火:`, bannerData);
  emit('click', bannerData);
  
  // 処理完了ログ
  console.log(`[BannerAd] #${currentClick} - クリック処理完了`);
};
</script>

<template>
  <!-- クリックイベントをもっとシンプルに -->
  <div class="app-icon" :style="iconStyles" @click="handleClick">
    <div class="product-name">{{ productName }}</div>
    <div v-if="discount" class="discount-label">{{ discount }}</div>
  </div>
</template>

<style scoped>
.app-icon {
  position: relative;
  border-radius: 24px;
  background: linear-gradient(to bottom, #f0f0f3, #dcdce0);
  box-shadow: 0 4px 10px rgba(0,0,0,0.2), inset 0 1px 2px rgba(255,255,255,0.6);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 6px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.app-icon:hover {
  transform: scale(1.05);
}

.product-name {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 8px;
  font-weight: 500;
  color: white;
  text-align: center;
  text-shadow:
    0 0 1px rgba(255, 255, 255, 1),
    0 0 2px rgba(255, 255, 255, 1),
    -1px -1px 0 rgba(0, 0, 0, 0.7),
    1px -1px 0 rgba(0, 0, 0, 0.7),
    -1px 1px 0 rgba(0, 0, 0, 0.7),
    1px 1px 0 rgba(0, 0, 0, 0.7);
  background-color: rgba(0, 0, 0, 0.4);
  padding: 2px 4px;
  border-radius: 8px;
  max-width: 90%;
  margin-bottom: 1px;
}

.discount-label {
  position: absolute;
  top: 6px;
  right: 6px;
  background: #e60023;
  color: white;
  padding: 2px 6px;
  font-size: 8px;
  font-weight: bold;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  white-space: nowrap;
}
</style>
